import { EntityManager } from '../../../engine/ecs/core/EntityManager';
import { ComponentManager } from '../../../engine/ecs/core/ComponentManager';
import { Entity } from '../../../engine/ecs/core/Entity';
import { TransformComponent } from '../../../engine/ecs/components/TransformComponent';
import {
  SpriteComponent,
  RenderingType,
} from '../../../engine/ecs/components/SpriteComponent';
import {
  CollisionComponent,
  CollisionShape,
} from '../../../engine/ecs/components/CollisionComponent';
import {
  CollectibleComponent,
  CollectibleType,
} from '../components/CollectibleComponent';
import { Vector2 } from '../../../engine/ecs/core/Vector2';
import { MazeLevel, CellType } from '../data/MazeData';

/**
 * MazeEntityFactory - Creates maze elements
 */
export class MazeEntityFactory {
  private entityManager: EntityManager;
  private componentManager: ComponentManager;

  constructor() {
    this.entityManager = EntityManager.getInstance();
    this.componentManager = ComponentManager.getInstance();
  }

  /**
   * Create the entire maze from maze data
   */
  public createMaze(mazeData: MazeLevel): Entity[] {
    const cellSize = mazeData.cellSize;
    const entities: Entity[] = [];

    for (let y = 0; y < mazeData.height; y++) {
      for (let x = 0; x < mazeData.width; x++) {
        const cellType = mazeData.maze[y][x];
        const pixelX = x * cellSize + cellSize / 2;
        const pixelY = y * cellSize + cellSize / 2;

        switch (cellType) {
          case CellType.Wall:
            entities.push(this.createWall(pixelX, pixelY, cellSize));
            break;
          case CellType.Dot:
            entities.push(this.createDot(pixelX, pixelY));
            break;
          case CellType.PowerPellet:
            entities.push(this.createPowerPellet(pixelX, pixelY));
            break;
          // Pacman and Ghost spawn points are handled by their respective factories
        }
      }
    }

    return entities;
  }

  /**
   * Create a wall entity
   */
  private createWall(x: number, y: number, size: number): Entity {
    const entity = this.entityManager.createEntity();

    // Transform component
    const transform = new TransformComponent(
      new Vector2(x, y),
      0,
      new Vector2(1, 1)
    );
    this.componentManager.addComponent(entity, transform);

    // Sprite component - blue wall
    const sprite = new SpriteComponent(
      '#000080', // Dark blue wall color (more classic Pacman look) 0000FF
      new Vector2(size, size), // Full cell size
      undefined, // no texture
      true, // visible
      1.0, // opacity
      0, // z-index (background)
      RenderingType.Rectangle // Use generic rectangle rendering for walls
    );
    this.componentManager.addComponent(entity, sprite);

    // Collision component - solid wall
    const collision = new CollisionComponent(
      CollisionShape.Rectangle, // rectangular collision
      new Vector2(size, size), // collision size
      new Vector2(0, 0), // offset
      false, // not a trigger
      'wall', // collision layer
      [] // walls don't actively collide
    );
    this.componentManager.addComponent(entity, collision);

    return entity;
  }

  /**
   * Create a dot entity
   */
  private createDot(x: number, y: number): Entity {
    const entity = this.entityManager.createEntity();

    // Transform component
    const transform = new TransformComponent(
      new Vector2(x, y),
      0,
      new Vector2(1, 1)
    );
    this.componentManager.addComponent(entity, transform);

    // Sprite component - small yellow dot
    const sprite = new SpriteComponent(
      '#FFFF00', // Yellow color
      new Vector2(4, 4), // Small dot size
      undefined, // no texture
      true, // visible
      1.0, // opacity
      1, // z-index
      RenderingType.Circle // Use generic circle rendering for dots
    );
    this.componentManager.addComponent(entity, sprite);

    // Collision component - trigger for collection
    const collision = new CollisionComponent(
      CollisionShape.Circle, // circular collision
      new Vector2(6, 6), // collision size
      new Vector2(0, 0), // offset
      true, // is a trigger
      'collectible', // collision layer
      ['pacman'] // only pacman can collect
    );
    this.componentManager.addComponent(entity, collision);

    // Collectible component
    const collectible = new CollectibleComponent(
      CollectibleType.Dot, // dot type
      10 // 10 points
    );
    this.componentManager.addComponent(entity, collectible);

    return entity;
  }

  /**
   * Create a power pellet entity
   */
  private createPowerPellet(x: number, y: number): Entity {
    const entity = this.entityManager.createEntity();

    // Transform component
    const transform = new TransformComponent(
      new Vector2(x, y),
      0,
      new Vector2(1, 1)
    );
    this.componentManager.addComponent(entity, transform);

    // Sprite component - large yellow pellet
    const sprite = new SpriteComponent(
      '#FFFF00', // Yellow color
      new Vector2(12, 12), // Larger size
      undefined, // no texture
      true, // visible
      1.0, // opacity
      1, // z-index
      RenderingType.PowerPellet // Use generic power pellet rendering
    );
    this.componentManager.addComponent(entity, sprite);

    // Collision component - trigger for collection
    const collision = new CollisionComponent(
      CollisionShape.Circle, // circular collision
      new Vector2(14, 14), // collision size
      new Vector2(0, 0), // offset
      true, // is a trigger
      'collectible', // collision layer
      ['pacman'] // only pacman can collect
    );
    this.componentManager.addComponent(entity, collision);

    // Collectible component - power pellet
    const collectible = new CollectibleComponent(
      CollectibleType.PowerPellet, // power pellet type
      50 // 50 points
    );
    this.componentManager.addComponent(entity, collectible);

    return entity;
  }

  /**
   * Create a bonus fruit entity
   */
  public createBonusFruit(
    x: number,
    y: number,
    fruitType: CollectibleType,
    points: number
  ): Entity {
    const entity = this.entityManager.createEntity();

    // Transform component
    const transform = new TransformComponent(
      new Vector2(x, y),
      0,
      new Vector2(1, 1)
    );
    this.componentManager.addComponent(entity, transform);

    // Sprite component - fruit color (varies by type)
    let color = '#FF0000'; // Default red
    switch (fruitType) {
      case CollectibleType.Cherry:
        color = '#FF0000';
        break;
      case CollectibleType.Strawberry:
        color = '#FF69B4';
        break;
      case CollectibleType.Orange:
        color = '#FFA500';
        break;
      case CollectibleType.Apple:
        color = '#FF0000';
        break;
      default:
        color = '#FF0000';
    }

    const sprite = new SpriteComponent(
      color, // Fruit color
      new Vector2(16, 16), // Medium size
      undefined, // no texture
      true, // visible
      1.0, // opacity
      1 // z-index
    );
    this.componentManager.addComponent(entity, sprite);

    // Collision component
    const collision = new CollisionComponent(
      CollisionShape.Circle, // circular collision
      new Vector2(16, 16), // collision size
      new Vector2(0, 0), // offset
      true, // is a trigger
      'collectible', // collision layer
      ['pacman'] // only pacman can collect
    );
    this.componentManager.addComponent(entity, collision);

    // Collectible component - bonus fruit
    const collectible = new CollectibleComponent(fruitType, points);
    this.componentManager.addComponent(entity, collectible);

    return entity;
  }
}
